import type {
  V1CreateBookingRequest,
  V1CreateBookingResponse,
} from '@ennismore/booking-api-client';
import { BookingServiceApi, Configuration } from '@ennismore/ows-api-client';
import { useMutation } from 'react-query';

import {
  APIDialect,
  getDefaultApiRequestOptions,
  useKongEnnismoreApiClientConfig,
} from '@/api';
import { useSkipRedirectSwitch } from '@/availability-fallback/hooks';
import { CreateBookingError } from '@/booking/errors';
import { OHIPBookingServiceClient } from '@/booking/services/ohip-booking.service';
import { mapNetworkError } from '@/errors';
import { getBookingGuestFromCount } from '@/hotel';
import { useSessionId } from '@/session/use-session-id.hook';

interface Request {
  body: V1CreateBookingRequest & { locale: { language: string } };
  dialect: APIDialect;
}

export const useCreateBookingMutation = () => {
  const [skipRedirect] = useSkipRedirectSwitch();
  const clientConfig = useKongEnnismoreApiClientConfig();
  const sessionId = useSessionId();

  return useMutation<V1CreateBookingResponse, CreateBookingError, Request>(
    async ({ body, dialect }) => {
      // @TODO Move this out to depend on an abstract service interface, resolving correct concrete for ohip/ows
      switch (dialect) {
        case 'ows': {
          try {
            const client = new BookingServiceApi(
              new Configuration({
                basePath: clientConfig.baseUrl,
                apiKey: clientConfig.apiKey,
              })
            );

            const { data } = await client.bookingServiceCreateBooking(
              {
                hotelReferenceId: body.hotelReferenceId,
                from: body.rooms[0].checkInDate,
                to: body.rooms[0].checkOutDate,
                rateCode: body.rooms[0].rateCode,
                roomRequest: body.rooms.map((room) => ({
                  guest: getBookingGuestFromCount(room.guests),
                  roomCode: room.roomCode,
                  twin: false, // TODO: hardcoded, fix please
                })),
              },
              getDefaultApiRequestOptions(body.locale.language, {
                skipRedirect,
              })
            );
            return data;
          } catch (e) {
            console.error(e);
            throw new CreateBookingError(await mapNetworkError(e));
          }
        }
        case 'ohip':
          return await new OHIPBookingServiceClient(clientConfig).createBooking(
            { ...body, sessionId }
          );
      }
    }
  );
};
